import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, MapPin, Phone, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getDivision, DIVISIONS } from "@/data/divisions";
import { useListings } from "@/lib/cms";

const DivisionDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const division = getDivision(slug);
  const { items: liveListings } = useListings(slug);

  if (!division) {
    return (
      <div className="pt-40 px-6 md:px-12 lg:px-24 min-h-screen">
        <p className="uppercase text-xs tracking-[0.2em] text-slate-500">Not Found</p>
        <h1 className="font-heading text-3xl text-slate-900 mt-3">Division not found</h1>
        <Button
          data-testid="back-home"
          onClick={() => navigate("/")}
          className="mt-8 rounded-full bg-[#0A2540] text-white"
        >
          <ArrowLeft size={16} className="mr-2" /> Back to Home
        </Button>
      </div>
    );
  }

  return (
    <div data-testid={`division-page-${division.slug}`} className="pt-32 pb-24 bg-white">
      <section className="px-6 md:px-12 lg:px-24">
        <button
          data-testid="division-back-button"
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8 items-center">
          <div className="lg:col-span-6">
            <p className="uppercase text-xs tracking-[0.25em] font-semibold text-[#0284C7]">
              Division · 0{DIVISIONS.findIndex((d) => d.slug === division.slug) + 1}
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-slate-900 mt-5 leading-[1.02]">
              {division.name}
            </h1>
            <p className="font-heading text-xl sm:text-2xl text-[#0A2540] font-medium mt-4">
              {division.tagline}
            </p>
            <p className="text-slate-600 leading-relaxed mt-6 max-w-xl">{division.summary}</p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Button
                data-testid="division-cta-book"
                onClick={() => navigate(`/book?division=${division.slug}`)}
                className="rounded-full bg-[#0A2540] hover:bg-[#0F172A] text-white h-14 px-7"
              >
                Book Appointment <ArrowUpRight size={18} className="ml-1" strokeWidth={1.5} />
              </Button>
              <Link to="/#contact">
                <Button
                  variant="outline"
                  className="rounded-full h-14 px-7 border-slate-300 hover:bg-slate-50"
                >
                  Contact Team
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-tr from-[#0A2540]/10 to-[#0284C7]/10 rounded-[2rem] -z-10 blur-2xl" />
              <img
                src={division.image}
                alt={division.name}
                className="rounded-3xl w-full h-[500px] object-cover shadow-[0_30px_80px_rgba(10,37,64,0.18)]"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 mt-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {division.stats.map((s) => (
            <div
              key={s.k}
              className="rounded-3xl bg-[#F8FAFC] border border-slate-100 p-8"
            >
              <p className="font-heading text-4xl font-medium text-slate-900">{s.v}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-2">{s.k}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500">Services</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 mt-4 leading-[1.1]">
              What we offer.
            </h2>
            <p className="text-slate-600 mt-6 max-w-md leading-relaxed">
              A comprehensive portfolio of services delivered with operational
              precision and client-first values.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {division.services.map((s) => (
              <div
                key={s}
                className="card-lift rounded-2xl bg-white border border-slate-100 p-6 flex items-start gap-3"
              >
                <span className="w-8 h-8 rounded-lg bg-[#0A2540] text-white flex items-center justify-center flex-shrink-0">
                  <Check size={14} strokeWidth={2} />
                </span>
                <p className="font-medium text-slate-800">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {liveListings !== null && liveListings.length > 0 && (
        <section
          data-testid="featured-listing-section"
          className="px-6 md:px-12 lg:px-24 mt-24"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} strokeWidth={1.5} className="text-[#0284C7]" />
            <p className="uppercase text-xs tracking-[0.25em] font-semibold text-[#0284C7]">
              Featured Listings
            </p>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-[1.1] max-w-2xl">
              Current properties from Indo-Pacific Group.
            </h2>
            <p className="text-slate-600 max-w-md text-sm leading-relaxed">
              Hand-picked residences across NCR. Tap any listing to call us or
              book a site visit instantly.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-7">
            {liveListings.map((listing) => (
              <article
                key={listing.id}
                data-testid={`listing-${listing.id}`}
                className="group card-lift rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgba(10,37,64,0.04)] flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={listing.flyer}
                    alt={listing.title}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-700"
                    loading="lazy"
                  />
                  {listing.tag && (
                    <span className="absolute top-4 left-4 backdrop-blur-md bg-white/85 border border-white/60 rounded-full px-3 py-1.5 text-[11px] font-semibold text-slate-800 uppercase tracking-wider">
                      {listing.tag}
                    </span>
                  )}
                </div>

                <div className="p-6 lg:p-8 flex-1 flex flex-col">
                  <h3 className="font-heading text-xl lg:text-2xl font-medium text-slate-900 leading-tight">
                    {listing.title}
                  </h3>
                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                    <MapPin size={14} strokeWidth={1.5} />
                    {listing.location}
                  </div>

                  <div className="mt-5 pb-5 border-b border-slate-100">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">
                      Asking Price
                    </p>
                    <p className="font-heading text-2xl lg:text-3xl font-medium text-[#0A2540] mt-1">
                      {listing.price}
                    </p>
                  </div>

                  <ul className="mt-5 space-y-2.5 flex-1">
                    {listing.highlights.slice(0, 4).map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
                        <span className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={11} strokeWidth={2.5} className="text-emerald-600" />
                        </span>
                        {h}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-7 flex flex-col sm:flex-row gap-3">
                    <a
                      data-testid={`listing-call-${listing.id}`}
                      href={`tel:${listing.phone}`}
                      className="inline-flex items-center justify-center gap-2 flex-1 rounded-full bg-[#0A2540] hover:bg-[#0F172A] text-white h-11 px-5 font-medium text-sm transition-colors"
                    >
                      <Phone size={14} strokeWidth={1.5} />
                      Call {listing.phone}
                    </a>
                    <Button
                      data-testid={`listing-book-${listing.id}`}
                      variant="outline"
                      onClick={() =>
                        navigate(
                          `/book?division=${division.slug}&listing=${encodeURIComponent(listing.title)}`
                        )
                      }
                      className="rounded-full h-11 px-5 border-slate-300 hover:bg-slate-50 text-sm"
                    >
                      Book Appointment
                      <ArrowUpRight size={14} strokeWidth={1.5} className="ml-1" />
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="px-6 md:px-12 lg:px-24 mt-24">
        <div className="rounded-3xl bg-[#0A2540] text-white p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h3 className="font-heading text-3xl md:text-4xl font-light leading-tight">
              Ready to start with {division.name}?
            </h3>
            <p className="text-slate-300 mt-4 max-w-xl">
              Book an appointment with our specialists. Our team will reach out
              to confirm your slot within one business day.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Button
              data-testid="division-cta-book-bottom"
              onClick={() => navigate(`/book?division=${division.slug}`)}
              className="rounded-full bg-white text-[#0A2540] hover:bg-slate-100 h-14 px-7"
            >
              Book Appointment
              <ArrowUpRight size={18} className="ml-1" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DivisionDetail;
